"use client"

import { createContext, useContext } from 'react';

import { useAnchorEl } from '../../..';

import type { MenuProps } from '@mui/material';
import type { Dispatch, FC, MouseEvent, ReactNode, SetStateAction } from 'react';

export interface MenuProviderProps {
  children: ReactNode;
}

type UseAnchorElReturn = ReturnType<typeof useAnchorEl>;

interface MenuState {
  open: boolean;
  anchorEl: MenuProps['anchorEl'] | undefined;
  setAnchorEl: Dispatch<SetStateAction<MenuProps['anchorEl']>>;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  onClose: UseAnchorElReturn['handleClose'];
  onMouseEnter: UseAnchorElReturn['handleMouseEnter'];
  onMouseLeave: UseAnchorElReturn['handleMouseLeave'];
}

const initialState: MenuState = {
  open: false,
  anchorEl: void 0,
  setAnchorEl: (element) => element,
  onClick: () => void 0,
  onClose: () => void 0,
  onMouseEnter: () => void 0,
  onMouseLeave: () => void 0,
};

export const Context = createContext<MenuState>( initialState );

export const useMenuContext = (): MenuState => useContext<MenuState>( Context );

/**
 * provides context for controlling the Menu State
 * requires a <PopperToggle> to consume the toggle handler
 * requires a <PopoverDisplay> to consume event handler
 *
 * @example
 * ```jsx
 *   <PopperProvider>
 *     <PopperToggle>
 *       ...
 *     </PopperToggle>
 *     <PopoverDisplay>
 *       ...
 *     </PopoverDisplay>
 *   </PopperProvider>
 * ```
 */

export const MenuProvider: FC<MenuProviderProps> = ({ children }) => {
  const {
    anchorEl,
    setAnchorEl,
    handleClose,
    handleClick,
    handleMouseLeave,
    handleMouseEnter,
  } = useAnchorEl();

  const open = Boolean( anchorEl );

  const state = {
    open,
    anchorEl,
    setAnchorEl,
    onClose: handleClose,
    onClick: handleClick,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  };

  return (
    <Context.Provider value={state}>
      {children}
    </Context.Provider>
  );
};
